// PrintReceipt.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";
import { format } from "date-fns";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import AGO_Logo from "../../../Assets/Images/AGO-Logo.png";
import { AccountsandFinanceurl, companyManagementUrl, userManagementUrl } from "../../../config";
import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL || 'https://matrix.mcb.co.ke/usermanagement';

const baseURL2 = companyManagementUrl.uri;

const LOGO_ORIGIN = (() => {
  try {
    return new URL(baseURL2).origin;
  } catch {
    return 'https://matrix.mcb.co.ke';
  }
})();
 
// ---- Logo path normalization (identical logic to PrintInvoice.jsx, ----
// ---- except it resolves against LOGO_ORIGIN instead of baseURL)     ----
const normalizeLogoPath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;
 
  const trimmed = rawPath.trim();
  if (!trimmed) return null;
 
  const isWindowsPath = /^[a-zA-Z]:[\\/]/.test(trimmed);
  if (isWindowsPath) return null;
 
  let cleaned = trimmed.replace(/^(\.\.\/|\.\/)+/, '');
  cleaned = cleaned.replace(/^\/+/, '');
 
  if (!cleaned.startsWith('assets/')) {
    return null;
  }
 
  return `${LOGO_ORIGIN.replace(/\/+$/, '')}/${cleaned}`;
};


// ---- Color extraction helpers ----
const DEFAULT_COLORS = { primary: "#8A3533", secondary: "#9EBB3C" };

const toHex = (r, g, b) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;

const colorDistance = (a, b) =>
  Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

const luminance = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const [rl, gl, bl] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const readableTextColor = (hex) => (luminance(hex) > 0.5 ? "#1a1a1a" : "#ffffff");

const extractColorsFromImage = (imgSrc) => {
  return new Promise((resolve) => {
    if (!imgSrc) return resolve(DEFAULT_COLORS);

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const size = 60;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        const counts = {};
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          if (a < 200) continue;
          const brightness = (r + g + b) / 3;
          if (brightness > 235 || brightness < 20) continue;

          const key = [
            Math.round(r / 16) * 16,
            Math.round(g / 16) * 16,
            Math.round(b / 16) * 16,
          ].join(",");
          counts[key] = (counts[key] || 0) + 1;
        }

        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        if (sorted.length === 0) return resolve(DEFAULT_COLORS);

        const primaryRgb = sorted[0][0].split(",").map(Number);
        const primary = toHex(...primaryRgb);

        let secondary = DEFAULT_COLORS.secondary;
        for (let i = 1; i < sorted.length; i++) {
          const rgb = sorted[i][0].split(",").map(Number);
          if (colorDistance(primaryRgb, rgb) > 90) {
            secondary = toHex(...rgb);
            break;
          }
        }

        resolve({ primary, secondary });
      } catch (err) {
        console.error("Color extraction failed:", err);
        resolve(DEFAULT_COLORS);
      }
    };
    img.onerror = () => resolve(DEFAULT_COLORS);
    img.src = imgSrc;
  });
};
// -----------------------------------

const PrintReceipt = ({ data }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);
  const userId = parseInt(decodedToken.Id) || null;
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSignature, setUserSignature] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [companyData, setCompanyData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [brandColors, setBrandColors] = useState(DEFAULT_COLORS);

  console.log("Receipt Data:", data);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const companyQuery = `
          {
          allCompanies {
                id
                companyName
                companyEmail
                contactEmail
                contactEmailPassword
                emailDisplayName
                emailClient
                emailClientPortNumber
                subscriptionId
                createdBy
                createdDate
                updatedBy
                updatedDate
                logoPath
                address
                poBox
                phone
                mobile
                website
                pinVat
                tagline
            }
        }
        `;

        const response = await graphqlQuery(
          companyQuery,
          companyManagementUrl.uri,
        );

        if (response && response.length > 0) {
          const userCompanyId = companyId;
          const company = response.find(c => c.id === userCompanyId) 
                        || response[0];
          setCompanyData(company);
          console.log("Company Data1:", response[0]);

          const normalizedLogoUrl = normalizeLogoPath(company.logoPath);
          setLogoUrl(normalizedLogoUrl);
          setLogoLoadFailed(false);

          if (normalizedLogoUrl) {
            const colors = await extractColorsFromImage(normalizedLogoUrl);
            setBrandColors(colors);
          } else {
            setBrandColors(DEFAULT_COLORS);
          }
        }
      } catch (err) {
        console.error("Error fetching company data:", err);
      }
    };

    fetchCompanyData();
  }, []);

  console.log("Company Data:", companyData);

  useEffect(() => {
    const fetchLineItems = async () => {
      if (!data || !data[0]?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const receiptId = data[0].id;

        const receiptLinesQuery = `
          query {
            receiptLines(receiptLines: {receiptId: ${receiptId}}) {
              id
              receiptId
              feeNoteLineId
              accountSubCategoryId
              accountSubCategoryName
              description
              amount
              isActive
              createdBy
              createdDate
              updatedBy
              updatedDate
            }
          }
        `;

        const response = await graphqlQuery(
          receiptLinesQuery, 
          AccountsandFinanceurl.uri,
        );

        console.log("Receipt Line Items Response:", response);

        if (response && response.receiptLines) {
          setLineItems(response.receiptLines);
        } else if (response && Array.isArray(response)) {
          setLineItems(response);
        } else {
          setLineItems([]);
        }
      } catch (err) {
        console.error("Error fetching receipt lines:", err);
        setError("Failed to load receipt items");
        setLineItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLineItems();
  }, [data]);

  const fetchUserSignature = async () => {
    setSignatureLoading(true);
    try {
      console.log("Fetching signature for user:", data[0].approvedBy);

      const signatureQuery = `
        query {
          userSignature(userId: ${data[0].approvedBy})
        }
      `;

      const response = await axios.post(
        userManagementUrl.uri,
        {
          query: signatureQuery
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        }
      );

      console.log("Signature response:", response.data);

      if (response.data?.data?.userSignature) {
        const signaturePath = response.data.data.userSignature;
        const cleanPath = signaturePath.startsWith('/') ? signaturePath.substring(1) : signaturePath;
        const fullSignatureUrl = `${baseURL}/${cleanPath}`;

        console.log("Signature URL:", fullSignatureUrl);

        setUserSignature(fullSignatureUrl);
        setShowSignature(true);
        setSnackbar({
          open: true,
          message: 'Signature loaded successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'No signature found for user',
          severity: 'warning'
        });
      }
    } catch (err) {
      console.error("Error fetching signature:", err);
      setSnackbar({
        open: true,
        message: 'Failed to load signature',
        severity: 'error'
      });
    } finally {
      setSignatureLoading(false);
    }
  };

  const fetchSignatureAsBase64 = async () => {
    setSignatureLoading(true);
    try {
      console.log("Fetching signature for user:", data[0].approvedBy);

      const signatureQuery = `
        query {
          userSignature(userId: ${data[0].approvedBy})
        }
      `;

      const response = await axios.post(
        userManagementUrl.uri,
        { query: signatureQuery },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        }
      );

      if (response.data?.data?.userSignature) {
        const signaturePath = response.data.data.userSignature;
        const cleanPath = signaturePath.startsWith('/') ? signaturePath.substring(1) : signaturePath;
        const imageUrl = `${baseURL}/${cleanPath}`;

        console.log("Fetching image from:", imageUrl);

        const imageResponse = await axios.get(imageUrl, {
          responseType: 'arraybuffer',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/"/g, '')}`
          }
        });

        const base64 = btoa(
          new Uint8Array(imageResponse.data).reduce(
            (data, byte) => data + String.fromCharCode(byte),
            ''
          )
        );

        const contentType = imageResponse.headers['content-type'] ||
                           (signaturePath.endsWith('.png') ? 'image/png' :
                            signaturePath.endsWith('.jpg') || signaturePath.endsWith('.jpeg') ? 'image/jpeg' :
                            signaturePath.endsWith('.gif') ? 'image/gif' : 'image/png');

        const base64Image = `data:${contentType};base64,${base64}`;

        setUserSignature(base64Image);
        setShowSignature(true);
        setSnackbar({
          open: true,
          message: 'Signature loaded successfully',
          severity: 'success'
        });
      }
    } catch (err) {
      console.error("Error:", err);
      setSnackbar({
        open: true,
        message: 'Failed to load signature',
        severity: 'error'
      });
    } finally {
      setSignatureLoading(false);
    }
  };

  const toggleSignature = () => {
    if (!showSignature) {
      fetchUserSignature();
    } else {
      setShowSignature(false);
      setUserSignature(null);
      setSnackbar({
        open: true,
        message: 'Signature removed',
        severity: 'info'
      });
    }
  };

  const primaryText = readableTextColor(brandColors.primary);
  const secondaryText = readableTextColor(brandColors.secondary);

  const printStyles = `
    @media print {
      body * {
        visibility: hidden;
      }
      .print-section, .print-section * {
        visibility: visible;
      }
      .print-section {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: white;
        padding: 0;
        margin: 0;
        transform: scale(0.95);
        transform-origin: top left;
      }
      .no-print {
        display: none !important;
      }
      .loading-section {
        display: none !important;
      }
      @page {
        margin: 10mm;
        size: A4 portrait;
      }
    }

    .print-container {
      min-height: 100vh;
      background: white;
      padding: 20px;
    }

    .print-section {
      padding: 25px;
      font-family: 'Georgia', 'Times New Roman', serif;
      background: white;
      max-width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      position: relative;
      border: 3px double ${brandColors.primary};
      box-shadow: 0 0 20px rgba(138, 53, 51, 0.1);
      font-size: 14px;
    }

    .loading-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      text-align: center;
    }

    .error-section {
      background: #ffe6e6;
      border: 1px solid #ff4444;
      padding: 20px;
      border-radius: 5px;
      text-align: center;
      margin: 20px;
    }

    .receipt-header {
      text-align: center;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 3px double ${brandColors.primary};
    }

    .receipt-logo-container {
      margin-bottom: 15px;
    }

    .receipt-logo {
      max-width: 180px;
      height: auto;
      max-height: 90px;
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }

    .logo-placeholder {
      width: 180px;
      height: 90px;
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px double ${brandColors.primary};
      color: ${primaryText};
      font-weight: bold;
      font-size: 18px;
      text-align: center;
      line-height: 1.2;
      margin: 0 auto;
    }

    .receipt-title {
      font-size: 28px;
      font-weight: bold;
      color: ${brandColors.primary};
      margin: 10px 0;
      text-transform: uppercase;
      letter-spacing: 2px;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
    }

    .receipt-subtitle {
      font-size: 16px;
      color: ${brandColors.secondary};
      font-style: italic;
      margin-bottom: 10px;
    }

    .firm-info {
      font-size: 12px;
      color: #333;
      line-height: 1.4;
      margin-bottom: 5px;
    }

    .receipt-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 25px;
      padding: 20px;
      background: linear-gradient(135deg, #f8f8f8 0%, #f0f0f0 100%);
      border: 1px solid #ddd;
      border-radius: 8px;
    }

    .receipt-detail-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .receipt-detail-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 0;
      border-bottom: 1px dotted #ccc;
    }

    .receipt-detail-label {
      font-weight: bold;
      color: ${brandColors.primary};
      min-width: 120px;
    }

    .receipt-detail-value {
      color: #333;
      font-weight: 500;
      text-align: right;
    }

    .payment-info {
      background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
      border: 2px solid ${brandColors.secondary};
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 25px;
    }

    .payment-title {
      font-weight: bold;
      color: ${brandColors.primary};
      font-size: 16px;
      margin-bottom: 10px;
      text-align: center;
    }

    .payment-details {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }

    .receipt-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid ${brandColors.primary};
      margin: 20px 0;
      font-size: 13px;
      background: white;
      color: #333;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .receipt-table th {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      font-weight: bold;
      padding: 12px 8px;
      border: 1px solid ${brandColors.primary};
      text-align: left;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .receipt-table td {
      padding: 10px 8px;
      border: 1px solid #ddd;
      vertical-align: top;
      background: white;
      color: #333;
    }

    .receipt-table tr:nth-child(even) td {
      background-color: #f9f9f9;
    }

    .amount-column {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: 600;
      color: #333;
    }

    .receipt-summary {
      width: 300px;
      margin-left: auto;
      margin-top: 25px;
      border: 2px solid ${brandColors.primary};
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 3px 6px rgba(0,0,0,0.1);
    }

    .summary-table {
      width: 100%;
      border-collapse: collapse;
    }

    .summary-table td {
      padding: 12px 15px;
      border-bottom: 1px solid #ddd;
    }

    .summary-label {
      font-weight: bold;
      background: linear-gradient(135deg, #f9f9f9 0%, #e9e9e9 100%);
      color: ${brandColors.primary};
      font-size: 13px;
    }

    .summary-value {
      text-align: right;
      font-family: 'Courier New', monospace;
      font-weight: bold;
      color: #333;
      font-size: 13px;
    }

    .receipt-total {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText} !important;
      font-size: 16px;
    }

    .receipt-total .summary-value {
      color: ${primaryText} !important;
      font-weight: 800;
      font-size: 16px;
    }

    .receipt-footer {
      margin-top: 40px;
      padding-top: 25px;
      border-top: 3px double ${brandColors.secondary};
      text-align: center;
      font-size: 11px;
      color: #666;
    }

    .thank-you {
      font-size: 16px;
      font-weight: bold;
      color: ${brandColors.primary};
      margin-bottom: 15px;
      font-style: italic;
    }

    .signature-area {
      margin-top: 40px;
      text-align: center;
    }

    .signature-image {
      max-width: 150px;
      max-height: 60px;
      object-fit: contain;
      margin-bottom: 5px;
    }

    .signature-line {
      border-top: 2px solid ${brandColors.primary};
      width: 250px;
      margin: ${showSignature ? '10px' : '40px'} auto 0;
      padding-top: 8px;
      font-size: 12px;
      color: ${brandColors.primary};
      font-weight: 600;
    }

    .signature-note {
      font-size: 10px;
      margin-top: 5px;
      color: #666;
      font-style: italic;
    }

    .stamp-area {
      position: absolute;
      bottom: 100px;
      right: 50px;
      opacity: 0.8;
    }

    .stamp {
      border: 2px solid ${brandColors.primary};
      border-radius: 5px;
      padding: 10px;
      transform: rotate(5deg);
      background: white;
      font-size: 10px;
      font-weight: bold;
      color: ${brandColors.primary};
      text-align: center;
    }

    .print-controls {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      background: white;
      padding: 20px;
      border-radius: 10px;
      box-shadow: 0 6px 20px rgba(0,0,0,0.3);
      border: 3px solid ${brandColors.primary};
      display: flex;
      gap: 15px;
    }

    .print-btn {
      background: linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primary} 100%);
      color: ${primaryText};
      border: none;
      padding: 12px 25px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.3s ease;
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }

    .print-btn:hover {
      transform: translateY(-2px);
      opacity: 0.88;
      box-shadow: 0 5px 12px rgba(0,0,0,0.3);
    }

    .signature-btn {
      background: ${showSignature 
        ? 'linear-gradient(135deg, #666 0%, #555 100%)' 
        : `linear-gradient(135deg, ${brandColors.secondary} 0%, ${brandColors.secondary} 100%)`};
      color: ${showSignature ? '#ffffff' : secondaryText};
      border: none;
      padding: 12px 25px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.3s ease;
      min-width: 140px;
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }

    .signature-btn:hover {
      transform: translateY(-2px);
      opacity: 0.9;
      box-shadow: 0 5px 12px rgba(0,0,0,0.3);
    }

    .signature-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .close-btn {
      background: linear-gradient(135deg, #666 0%, #555 100%);
      color: white;
      border: none;
      padding: 12px 25px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      transition: all 0.3s ease;
      box-shadow: 0 3px 8px rgba(0,0,0,0.2);
    }

    .close-btn:hover {
      background: linear-gradient(135deg, #555 0%, #666 100%);
      transform: translateY(-2px);
      box-shadow: 0 5px 12px rgba(0,0,0,0.3);
    }
  `;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const handleImageError = (e) => {
    setLogoLoadFailed(true);
    const img = e.target;
    img.src = AGO_Logo;
  };

  const receipt = data?.[0] || {};

  const totalAmount = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  const companyInfo = {
    name: companyData?.companyName || "set at company management",
    address: companyData?.address || "set at company management",
    poBox: companyData?.poBox || "set at company management",
    phone: companyData?.phone || "set at company management",
    mobile: companyData?.mobile || "set at company management",
    email: companyData?.companyEmail || "set at company management",
    website: companyData?.website || "set at company management",
    pinVat: companyData?.pinVat || "set at company management",
    tagline: companyData?.tagline || "set at company management"
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="loading-section">
          <CircularProgress size={40} style={{ marginBottom: 20 }} />
          <Typography variant="h6">Loading Receipt Details...</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            Fetching receipt items for Receipt #{receipt.id}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="print-container">
        <style>{printStyles}</style>
        <Box className="error-section">
          <Typography variant="h6" color="error">Error Loading Receipt</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}>
            {error}
          </Typography>
          <button 
            className="print-btn" 
            onClick={() => window.location.reload()}
            style={{ marginTop: 15 }}
          >
            Retry
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="print-container">
      <style>{printStyles}</style>

      <Box className="print-controls no-print">
        <button 
          className="print-btn" 
          onClick={handlePrint}
        >
          🖨️ Print
        </button>
        <button 
          className="signature-btn" 
          onClick={toggleSignature}
          disabled={signatureLoading}
        >
          {signatureLoading ? (
            <CircularProgress size={16} style={{ color: 'inherit', marginRight: 8 }} />
          ) : null}
          {showSignature ? '✖ Manual Sign' : '🖊️ System Signature'}
        </button>
      </Box>

      <Box className="print-section">
        <div className="receipt-header">
          <div className="receipt-logo-container">
            <img 
              src={logoUrl && !logoLoadFailed ? logoUrl : AGO_Logo}
              alt={companyInfo.name}
              className="receipt-logo"
              onError={handleImageError}
            />
          </div>

          <div className="receipt-title">OFFICIAL RECEIPT</div>
          <div className="receipt-subtitle">{companyInfo.tagline}</div>

          <div className="firm-info">
            <div>{companyInfo.name}</div>
            <div>{companyInfo.address}</div>
            <div>{companyInfo.poBox}</div>
            <div>Tel: {companyInfo.phone} | Email: {companyInfo.email}</div>
            <div>PIN/VAT: {companyInfo.pinVat}</div>
          </div>
        </div>

        <div className="receipt-details">
          <div className="receipt-detail-group">
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Receipt No:</span>
              <span className="receipt-detail-value">
                {receipt.referenceNumber || `RCP-${receipt.id?.toString().padStart(4, '0')}`}
              </span>
            </div>
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Date:</span>
              <span className="receipt-detail-value">{formatDate(receipt.receiptDate) || format(new Date(), 'dd/MM/yyyy')}</span>
            </div>
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Client ID:</span>
              <span className="receipt-detail-value">{receipt.clientId || 'N/A'}</span>
            </div>
          </div>

          <div className="receipt-detail-group">
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Account:</span>
              <span className="receipt-detail-value">{receipt.accountName || 'N/A'}</span>
            </div>
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Status:</span>
              <span className="receipt-detail-value" style={{ 
                color: receipt.isPosted === 1 ? '#4CAF50' : '#FF9800',
                fontWeight: 'bold'
              }}>
                {receipt.isPosted === 1 ? 'POSTED' : 'PENDING'}
              </span>
            </div>
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Currency:</span>
              <span className="receipt-detail-value">{receipt.currencyName || 'KES'}</span>
            </div>
          </div>
        </div>

        <div className="payment-info">
          <div className="payment-title">PAYMENT DETAILS</div>
          <div className="payment-details">
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Mode of Payment:</span>
              <span className="receipt-detail-value">{receipt.modeOfReceiptName || 'N/A'}</span>
            </div>
            <div className="receipt-detail-item">
              <span className="receipt-detail-label">Reference:</span>
              <span className="receipt-detail-value">{receipt.referenceNumber || 'N/A'}</span>
            </div>
          </div>
        </div>

        <TableContainer component={Paper} elevation={0}>
          <Table className="receipt-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '40px' }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell style={{ width: '100px', textAlign: 'right' }}>Amount (KES)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.length > 0 ? (
                lineItems.map((item, index) => (
                  <TableRow key={item.id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      {item.description || "Payment received"}
                      {item.accountSubCategoryName && (
                        <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                          {item.accountSubCategoryName}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="amount-column">
                      {formatCurrency(item.amount)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                    No receipt items recorded
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="receipt-summary">
          <table className="summary-table">
            <tbody>
              <tr>
                <td className="summary-label">Subtotal:</td>
                <td className="summary-value">{formatCurrency(totalAmount)}</td>
              </tr>
              <tr>
                <td className="summary-label">Total Paid:</td>
                <td className="summary-value">{formatCurrency(totalAmount)}</td>
              </tr>
              <tr className="receipt-total">
                <td className="summary-label">AMOUNT RECEIVED:</td>
                <td className="summary-value">{formatCurrency(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="receipt-footer">
          <div className="thank-you">Thank you for your payment!</div>

          <div style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
            This receipt serves as confirmation of payment received. Please retain for your records.
          </div>

          <div className="signature-area">
            {showSignature && userSignature && (
              <img 
                src={userSignature} 
                alt="Signature" 
                className="signature-image"
                onError={(e) => {
                  console.error("Failed to load signature image:", e.target.src);
                  e.target.style.display = 'none';

                  const parent = e.target.parentElement;
                  if (parent) {
                    const existingError = parent.querySelector('.signature-error');
                    if (existingError) existingError.remove();

                    const errorMsg = document.createElement('div');
                    errorMsg.className = 'signature-error';
                    errorMsg.style.color = '#ff4444';
                    errorMsg.style.fontSize = '10px';
                    errorMsg.style.padding = '5px';
                    errorMsg.style.backgroundColor = '#ffeeee';
                    errorMsg.style.borderRadius = '3px';
                    errorMsg.style.marginBottom = '5px';
                    errorMsg.innerHTML = '⚠️ Signature failed to load';
                    parent.insertBefore(errorMsg, parent.firstChild);
                  }
                }}
                onLoad={(e) => {
                  console.log("Signature loaded successfully");
                  const parent = e.target.parentElement;
                  if (parent) {
                    const errorMsg = parent.querySelector('.signature-error');
                    if (errorMsg) errorMsg.remove();
                  }
                }}
              />
            )}
            <div className="signature-line">
              Authorized Signatory
            </div>
            <div className="signature-note">
              {showSignature ? 'Digitally Signed' : ''}
            </div>
          </div>

          <div className="stamp-area">
            <div className="stamp">
              RECEIVED<br/>
              {formatDate(receipt.receiptDate) || format(new Date(), 'dd/MM/yyyy')}
            </div>
          </div>

          <div style={{ marginTop: '25px', fontSize: '10px', color: '#999', fontStyle: 'italic' }}>
            Computer generated receipt • {showSignature ? 'Digitally signed' : 'Valid without signature'} • Printed on: {format(new Date(), 'dd/MM/yyyy HH:mm')}
          </div>
        </div>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PrintReceipt;
